import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap } from 'lucide-react';

const CaseStudies = () => {
  const caseStudies = [
    {
      title: 'StackForge: AI-Powered Full-Stack Training Platform',
      industry: 'Technical Education',
      subtitle: 'Accelerate Dev Skilling with AI-Powered Full-Stack Environments',
      challenge:
        'Existing training platforms tested technologies in isolation, offered no customization for specific tech stacks, and failed to mirror production complexity. This resulted in poor visibility into candidates\' actual capabilities and unreliable hiring decisions.',
      solution:
        'Built StackForge, an AI-powered platform using LangGraph and DSPy that generates complete full-stack applications with automated test suites. The system creates customized environments with project briefs, boilerplate code, and comprehensive tests across 10-15 tech stack combinations.',
      technicalHighlights: [
        'Multi-agent architecture with LangGraph for stateful generation workflow',
        'DSPy-powered critique module refined with human feedback for quality control',
        'LLM-based judge with continuous optimization feedback loop',
        'Exported planning checklist and rolling summarization for context management',
        'Separate solution generation consuming compiler and test case feedback',
      ],
      results: [
        '70%+ test coverage on auto-generated production-quality projects',
        'Verifiable, objective evaluation of architectural understanding',
        'Realistic end-to-end training mirroring real-world workflows',
        'Deep skill visibility beyond surface-level code challenges',
        'Support for 10-15 tech stack combinations covering 80%+ market demand',
      ],
      icon: GraduationCap,
      videoUrl: 'https://www.loom.com/embed/71f8ed5448fc4e6baaf3ae6b20a428c6',
    },
  ];

  return (
    <div className="bg-primary pt-16">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary to-primary/90">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-light text-text mb-6 text-center">
            Case <span className="text-secondary">Studies</span>
          </h1>
          <p className="text-xl text-muted text-center max-w-3xl mx-auto">
            Real implementations. Measurable results. See how we've helped businesses transform their operations with AI.
          </p>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
                <div
                  key={index}
                  className={`flex flex-col ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  } gap-8 items-center`}
                >
                  {/* Video */}
                  <div className="w-full md:w-1/2">
                    <div className="relative overflow-hidden rounded-lg shadow-2xl shadow-secondary/10">
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          src={study.videoUrl}
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          frameBorder="0"
                          allowFullScreen
                          title={study.title}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="w-full md:w-1/2 space-y-4">
                    <div>
                      <span className="text-sm text-secondary font-semibold uppercase tracking-wider">
                        {study.industry}
                      </span>
                      <h2 className="text-3xl font-bold text-white mt-2">
                        {study.title}
                      </h2>
                      {study.subtitle && (
                        <p className="text-lg text-muted mt-2 italic">
                          {study.subtitle}
                        </p>
                      )}
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-text mb-2">
                        Challenge
                      </h3>
                      <p className="text-muted leading-relaxed">
                        {study.challenge}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-text mb-2">
                        Solution
                      </h3>
                      <p className="text-muted leading-relaxed">
                        {study.solution}
                      </p>
                    </div>

                    {study.technicalHighlights && (
                      <div>
                        <h3 className="text-lg font-semibold text-text mb-3">
                          Technical Implementation
                        </h3>
                        <ul className="space-y-2">
                          {study.technicalHighlights.map((highlight, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-muted text-sm"
                            >
                              <span className="text-secondary mr-2">•</span>
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div>
                      <h3 className="text-lg font-semibold text-text mb-3">
                        Results
                      </h3>
                      <ul className="space-y-2">
                        {study.results.map((result, idx) => (
                          <li
                            key={idx}
                            className="flex items-start text-muted"
                          >
                            <span className="text-secondary mr-2 text-lg">
                              ✓
                            </span>
                            <span>{result}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-secondary to-secondary/80">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
            Ready to Create Your Success Story?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's discuss how we can help transform your business with AI solutions that deliver real results.
          </p>
          <Link
            to="/contact"
            className="px-8 py-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 font-medium inline-flex items-center gap-2"
          >
            Book a Free Consultation!
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
